import { useCallback, useEffect, useRef, useState } from 'react';
import type { GameApi } from '../api/games';
import type { GameId } from '../api/types';
import type { NewTicketInput, UseScannedTicketsValue } from './useScannedTickets';
import type { ScannedTicket } from './scannedTicketUtils';
import { track } from '../services/analyticsService';
import { matchAgainstGameDraw, normalizeDrawDate } from '../utils/ticketMatch';

interface TicketDetailSheetControllerArgs {
  tickets: ScannedTicket[];
  gameId: GameId;
  getGameDraw: GameApi['getGameDraw'];
  addTicket: UseScannedTicketsValue['addTicket'];
  updateTicket: UseScannedTicketsValue['updateTicket'];
  removeTicket: UseScannedTicketsValue['removeTicket'];
}

export function useTicketDetailSheetController({
  tickets,
  gameId,
  getGameDraw,
  addTicket,
  updateTicket,
  removeTicket,
}: TicketDetailSheetControllerArgs) {
  const [selectedTicket, setSelectedTicket] = useState<ScannedTicket | null>(null);
  const [undoTicket, setUndoTicket] = useState<ScannedTicket | null>(null);
  const [toastOpen, setToastOpen] = useState(false);
  const [refreshingTicketIds, setRefreshingTicketIds] = useState<Set<string>>(() => new Set());
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const triggerRef = useRef<HTMLElement | null>(null);

  const openTicket = useCallback((ticket: ScannedTicket, trigger?: HTMLElement | null) => {
    triggerRef.current = trigger ?? null;
    setRefreshError(null);
    setSelectedTicket(ticket);
  }, []);

  const closeSheet = useCallback(() => {
    setRefreshError(null);
    setSelectedTicket(null);
  }, []);

  useEffect(() => {
    if (!selectedTicket) return;
    const fresh = tickets.find((t) => t.id === selectedTicket.id);
    if (fresh && fresh !== selectedTicket) setSelectedTicket(fresh);
  }, [selectedTicket, tickets]);

  const handleRefreshTicket = useCallback(
    async (ticket: ScannedTicket) => {
      const drawDate = normalizeDrawDate(ticket.drawDate);
      if (!drawDate) {
        setRefreshError('This ticket needs a valid draw date before results can refresh.');
        return;
      }
      const ticketGameId = ticket.gameId ?? gameId;
      setRefreshError(null);
      setRefreshingTicketIds((prev) => new Set(prev).add(ticket.id));
      try {
        const gameDraw = await getGameDraw(ticketGameId, drawDate);
        const match = matchAgainstGameDraw(
          { main: ticket.main, bonus: ticket.bonus, drawDate },
          gameDraw
        );
        const nextPrize = match.prize > 0 ? match.prize : undefined;
        const nextMatch = winningMatchFromResult(match);
        const patch = {
          status: match.outcome,
          prize: nextPrize,
          drawDate,
          match: nextMatch,
        };
        await updateTicket(ticket.id, patch);
        track('ticket_result_refresh', {
          gameId: ticketGameId,
          outcome: match.outcome,
        });
        setSelectedTicket((prev) => (prev?.id === ticket.id ? { ...prev, ...patch } : prev));
      } catch {
        track('ticket_result_refresh', {
          gameId: ticketGameId,
          outcome: 'error',
          errorCode: 'refresh_failed',
        });
        setRefreshError('Could not refresh results. Try again in a moment.');
      } finally {
        setRefreshingTicketIds((prev) => {
          const next = new Set(prev);
          next.delete(ticket.id);
          return next;
        });
      }
    },
    [gameId, getGameDraw, updateTicket]
  );

  const handleDelete = useCallback(
    async (ticket: ScannedTicket) => {
      setSelectedTicket(null);
      setUndoTicket(ticket);
      setToastOpen(true);
      await removeTicket(ticket.id);
    },
    [removeTicket]
  );

  const handleUndo = useCallback(async () => {
    if (!undoTicket) return;
    const ticket = undoTicket;
    setToastOpen(false);
    setUndoTicket(null);
    await addTicket(ticketToInput(ticket));
  }, [addTicket, undoTicket]);

  return {
    selectedTicket,
    triggerRef,
    openTicket,
    closeSheet,
    handleDelete,
    handleRefreshTicket,
    refreshing: selectedTicket ? refreshingTicketIds.has(selectedTicket.id) : false,
    refreshError,
    toastOpen,
    setToastOpen,
    clearUndoTicket: () => setUndoTicket(null),
    handleUndo,
  };
}

function ticketToInput(ticket: ScannedTicket): NewTicketInput {
  return {
    id: ticket.id,
    scannedAt: ticket.scannedAt,
    main: ticket.main,
    bonus: ticket.bonus,
    drawDate: ticket.drawDate,
    cost: ticket.cost,
    status: ticket.status,
    prize: ticket.prize,
    source: ticket.source,
    confidence: ticket.confidence,
    gameId: ticket.gameId,
    match: ticket.match,
  };
}

function winningMatchFromResult(match: ReturnType<typeof matchAgainstGameDraw>): ScannedTicket['match'] | undefined {
  if (match.outcome !== 'jackpot' && match.outcome !== 'won-small') return undefined;
  if (match.matchedMain.length === 0 && match.matchedBonus.length === 0) return undefined;
  return { main: match.matchedMain, bonus: match.matchedBonus };
}
